import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ListCell } from '@cbhq/cds-web/cells';
import { Avatar } from '@cbhq/cds-web/media/Avatar';
import { ActionType, ComponentType } from '@cbhq/client-analytics';
import type { CoinbaseProfileMenuResource } from '@cbhq/identity-api-hooks/profile-menu/types';

import { useAnalytics } from '../hooks/useAnalytics';

const messages = defineMessages({
  legalEntity: {
    id: 'EntityPickerRow.legalEntity',
    defaultMessage: 'Business',
    description: 'Description for the legal entity resource type in the entity picker',
  },
  user: {
    id: 'EntityPickerRow.user',
    defaultMessage: 'Personal',
    description: 'Description for the personal resource type in the entity picker',
  },
});

type EntityPickerRowProps = {
  resource: CoinbaseProfileMenuResource;
  onPress: (resourceId: string) => void;
};

export function EntityPickerRow({ resource, onPress }: EntityPickerRowProps) {
  const { formatMessage } = useIntl();
  const { logEvent } = useAnalytics();

  const { name, identityAccountId, resourceType } = resource;
  if (!identityAccountId) {
    throw new Error('EntityPickerRow: identityAccountId is required');
  }

  const description = formatMessage(
    resourceType === 'legal_entity' ? messages.legalEntity : messages.user,
  );

  const handlePress = useCallback(() => {
    logEvent('entity_picker_row_click', {
      action: ActionType.click,
      componentType: ComponentType.button,
      resource_type: resourceType,
    });
    onPress(identityAccountId);
  }, [logEvent, onPress, identityAccountId, resourceType]);

  const media = <Avatar name={name} colorScheme="gray" shape="square" size="l" />;

  return (
    <ListCell
      spacingVariant="condensed"
      title={name}
      description={description}
      media={media}
      accessory="arrow"
      onClick={handlePress}
    />
  );
}
